/**
 * "Mejorar formas": reconoce el trazo a mano alzada y lo convierte en una figura limpia
 * (equivalente al Beautify / Enhance inked shapes de Microsoft Whiteboard).
 */
import type { GeoKind, GeoShape } from '../../shared/types.ts'
import type { InkPoint } from '../lib/freehand.ts'
import { simplifyInk } from '../lib/freehand.ts'
import { createGeo } from '../state/shapes.ts'

interface GeoParams {
  color: string
  strokeWidth: number
  createdBy: string
  z: number
}

export function recognizeShape(points: InkPoint[], params: GeoParams): GeoShape | null {
  if (points.length < 6) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let pathLength = 0
  for (let i = 0; i < points.length; i++) {
    const p = points[i]
    minX = Math.min(minX, p.x)
    maxX = Math.max(maxX, p.x)
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
    if (i > 0) pathLength += Math.hypot(p.x - points[i - 1].x, p.y - points[i - 1].y)
  }
  const w = maxX - minX
  const h = maxY - minY
  const diag = Math.hypot(w, h)
  if (diag < 24 || pathLength < 40) return null

  const first = points[0]
  const last = points[points.length - 1]
  const gap = Math.hypot(last.x - first.x, last.y - first.y)
  const closed = gap < diag * 0.28

  const make = (kind: GeoKind, box = { x: minX, y: minY, w, h }): GeoShape =>
    createGeo({
      kind,
      x: box.x,
      y: box.y,
      w: Math.max(box.w, 8),
      h: Math.max(box.h, 8),
      color: params.color,
      fill: 'none',
      strokeWidth: params.strokeWidth,
      createdBy: params.createdBy,
      z: params.z,
    })

  if (!closed) {
    // Trazo abierto y casi recto → línea.
    const chord = Math.hypot(last.x - first.x, last.y - first.y)
    if (chord / pathLength > 0.93 && chord > 30) {
      const shape = make('line', {
        x: first.x,
        y: first.y,
        w: last.x - first.x,
        h: last.y - first.y,
      })
      // Las líneas guardan dirección en w/h: permiten valores negativos.
      shape.w = last.x - first.x
      shape.h = last.y - first.y
      return shape
    }
    return null
  }

  // Cerrado: contar esquinas significativas.
  const corners = countCorners(points, diag)
  const circ = circularity(points, minX + w / 2, minY + h / 2)

  if (circ > 0.86 && corners <= 2) return make('ellipse')
  if (corners === 3) return make('triangle')
  if (corners === 4) {
    return isDiamond(points, minX, minY, w, h) ? make('diamond') : make('rectangle')
  }
  if (corners === 5 || corners === 6) return make('hexagon')
  if (circ > 0.8) return make('ellipse')
  return null
}

/** Número de vértices "reales" tras simplificar el polígono. */
function countCorners(points: InkPoint[], diag: number): number {
  const simplified = simplifyInk(points, diag * 0.055)
  // El primer y último punto son casi el mismo (trazo cerrado).
  const pts = simplified.slice(0, Math.max(1, simplified.length - 1))
  if (pts.length < 3) return pts.length

  let corners = 0
  for (let i = 0; i < pts.length; i++) {
    const prev = pts[(i - 1 + pts.length) % pts.length]
    const cur = pts[i]
    const next = pts[(i + 1) % pts.length]
    const a1 = Math.atan2(cur.y - prev.y, cur.x - prev.x)
    const a2 = Math.atan2(next.y - cur.y, next.x - cur.x)
    let delta = Math.abs(a2 - a1)
    while (delta > Math.PI) delta = Math.abs(delta - Math.PI * 2)
    if (delta > 0.55) corners++
  }
  return corners
}

/** 1 = círculo perfecto. Compara la dispersión del radio desde el centro. */
function circularity(points: InkPoint[], cx: number, cy: number): number {
  const radii = points.map((p) => Math.hypot(p.x - cx, p.y - cy))
  const mean = radii.reduce((a, b) => a + b, 0) / radii.length
  if (mean === 0) return 0
  const variance = radii.reduce((a, r) => a + (r - mean) ** 2, 0) / radii.length
  return Math.max(0, 1 - Math.sqrt(variance) / mean / 0.45)
}

/** Un rombo tiene sus vértices en los puntos medios de los lados del bounding box. */
function isDiamond(points: InkPoint[], minX: number, minY: number, w: number, h: number): boolean {
  const cornersNearBox = [
    { x: minX, y: minY },
    { x: minX + w, y: minY },
    { x: minX + w, y: minY + h },
    { x: minX, y: minY + h },
  ]
  const tolerance = Math.min(w, h) * 0.22
  let hits = 0
  for (const corner of cornersNearBox) {
    if (points.some((p) => Math.hypot(p.x - corner.x, p.y - corner.y) < tolerance)) hits++
  }
  return hits <= 2
}
